import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import TopNavbar from 'examples/Navbars/TopNavbar';
import Footer from 'examples/Footer';
import { useData } from '../../hooks';
import { apiGetCafe24Health } from '../../api/cafe24.api';

/**
 * 카페24 연동 상태 화면.
 *
 * 🔴 KEITI 전송 상태 화면과 같은 처방이다 — **없는 것을 눈에 보이게 만든다.**
 *    연동은 2026-05 부터 3개월 끊겨 있었는데, 실패가 서버 로그에만 남아 아무도 몰랐다.
 *    게다가 한 번 끊기면 갱신 스케줄러가 **영원히 포기한다**(사람이 재인가해야 낫는다).
 *    그래서 이 화면은 "지금 살아 있나" 보다 **"끊겼으면 무엇을 해야 하나"** 를 말한다.
 */

const LEVEL = {
  OK: { color: 'success', label: '정상' },
  WARN: { color: 'warning', label: '주의' },
  DOWN: { color: 'error', label: '끊김' },
};

const fmt = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

function Row({ label, value, strong }) {
  return (
    <MDBox display="flex" justifyContent="space-between" py={1}
      borderBottom="1px solid rgba(0,0,0,0.06)">
      <MDTypography variant="button" color="text">{label}</MDTypography>
      <MDTypography variant="button" fontWeight={strong ? 'bold' : 'regular'}>
        {value}
      </MDTypography>
    </MDBox>
  );
}

function Cafe24Health() {
  const { user } = useData();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await apiGetCafe24Health(user?.accessToken));
    } catch (e) {
      // 🔴 삼키지 않는다. 조회 실패를 "정상" 으로 보여주면 이 화면의 존재 이유가 없어진다.
      setError('연동 상태를 불러오지 못했습니다. 새로고침을 눌러 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  }, [user?.accessToken]);

  useEffect(() => { load(); }, [load]);

  const lv = LEVEL[data?.level] || LEVEL.OK;

  return (
    <DashboardLayout>
      <TopNavbar absolute isMini />
      <MDBox mt={8} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8} lg={7}>
            <Card>
              <MDBox p={3}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <MDTypography variant="h5">제로살롱몰(카페24) 연동 상태</MDTypography>
                  <MDButton size="small" variant="outlined" color="info" onClick={load}>
                    새로고침
                  </MDButton>
                </MDBox>

                {loading && <MDTypography variant="button" color="text">불러오는 중…</MDTypography>}

                {!loading && error && (
                  <MDTypography variant="button" color="error">{error}</MDTypography>
                )}

                {!loading && !error && data && (
                  <>
                    <MDBox display="flex" alignItems="center" mb={2}>
                      <MDBadge badgeContent={lv.label} color={lv.color} variant="gradient"
                        size="lg" container />
                      <MDTypography variant="button" fontWeight="bold" ml={2}>
                        {data.status}
                      </MDTypography>
                    </MDBox>

                    {/* 🔴 끊겼을 때 "무엇을 해야 하는지" 를 화면이 직접 말한다 */}
                    {data.reason ? (
                      <MDBox mb={2} p={2} borderRadius="lg"
                        bgColor={data.level === 'DOWN' ? 'error' : 'warning'} variant="gradient">
                        <MDTypography variant="button" color="white" fontWeight="medium">
                          {data.reason}
                        </MDTypography>
                      </MDBox>
                    ) : null}

                    <Row label="몰 아이디" value={data.mallId} />
                    <Row label="access token 만료" value={fmt(data.accessTokenExpiresAt)} />
                    <Row label="refresh token 만료" value={fmt(data.refreshTokenExpiresAt)} strong />
                    <Row label="refresh 남은 시간"
                      value={data.refreshExpiresInHours === null ? '-' : `${data.refreshExpiresInHours}시간`} />
                    <Row label="마지막 갱신" value={fmt(data.updatedAt)} />
                    <Row label="마지막 전환" value={fmt(data.lastConversionAt)} />
                    <Row label="전환 누적" value={`${data.conversionCount}건`} />
                    <Row label="최근 7일 전환 실패" value={`${data.recentFailCount}건`} />

                    <MDBox mt={3}>
                      <MDTypography variant="caption" color="text">
                        연동이 끊기면 회원이 R포인트를 제로살롱몰 적립금으로 바꾸지 못합니다.
                        갱신은 5분마다 자동으로 돌지만, <b>한 번 끊기면 스스로 복구되지 않습니다</b> —
                        몰 관리자 계정으로 재인가해야 합니다.
                      </MDTypography>
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  /** 굵게 강조할지 — refresh 만료처럼 눈에 띄어야 하는 값에 쓴다 */
  strong: PropTypes.bool,
};

Row.defaultProps = { strong: false };

export default Cafe24Health;
